// MercadoCalendario — vista CALENDARIO de demanda de mercado para una
// propiedad (vista CLIENTE). Pinta TODO el tramo check-in → check-out de cada
// combo (no solo el día de inicio) según su OCUPACIÓN estimada del mercado, y al
// hacer click en un día con datos muestra el detalle de las estadías que lo
// cubren. Permite navegar meses/años (incluido el PASADO).
//
// Round 2026-07-23: el cliente ve SOLO la ocupación estimada (0..100, proyectada
// al check-in, redondeada al 10%) y su nivel de demanda — NUNCA precios. El
// color sale de la PALETA elegida por el admin en Parámetros (viaja en la
// respuesta como `paletaId`; los colores viven en utils/mercadoPaletas.ts).
//
// La usan la página del cliente (/mercado-cliente) y el tab "Vista cliente" de
// /mercado (preview admin — ve EXACTAMENTE lo mismo). Consume
// GET /api/mercado/cliente/calendario (rango libre, minPuntos=1). Filosofía de
// docs/modulo-mercado.md §7.3: solo ocupación estimada, nunca datos crudos.
import React, { useEffect, useMemo, useState } from "react";
import { Alert, Button, Empty, Segmented, Skeleton, Space, Tag, Tooltip } from "antd";
import { LeftOutlined, PushpinFilled, RightOutlined } from "@ant-design/icons";
import dayjs, { Dayjs } from "dayjs";
import "dayjs/locale/es";
import { mercadoApi, MercadoCalendarioResponse, MercadoFechaCliente } from "../../services/mercado.api";
import {
  getPaleta,
  colorDeOcupacion,
  nivelDeOcupacion,
  nivelLabel,
  redondearOcupacion10,
  tinte,
  NIVELES_DESC,
  colorDeNivel,
} from "../../utils/mercadoPaletas";
import { notasEnRango, notasPorDia } from "./notas.util";
import "./mercado.css";
import { ahoraChile } from "../../utils/fechaChile";

dayjs.locale("es");

const DOW = ["lun", "mar", "mié", "jue", "vie", "sáb", "dom"];
const MESES = [
  "enero", "febrero", "marzo", "abril", "mayo", "junio",
  "julio", "agosto", "septiembre", "octubre", "noviembre", "diciembre",
];

/** Representante de un día cubierto por varias estadías: la de MAYOR ocupación —
 * así un aviso de demanda alta nunca queda oculto por una estadía de baja.
 * (`porDia` ya incluye solo combos con ocupación no nula.) */
function repOcupacion(combos: MercadoFechaCliente[]): MercadoFechaCliente | null {
  let best: MercadoFechaCliente | null = null;
  for (const c of combos) {
    if (c.ocupacion == null) continue;
    if (!best || c.ocupacion > (best.ocupacion ?? -1)) best = c;
  }
  return best;
}

interface Props {
  propiedadId: number;
  /** Nombre para el encabezado (opcional). */
  nombre?: string;
  /**
   * Sube cuando hay datos nuevos de mercado (ingesta, avisos, config). El
   * calendario se refresca EN SU LUGAR: sin skeleton, sin cerrar el día abierto
   * y sin pisar lo que se ve si el refresco falla. Round 2026-09-11.
   */
  refreshToken?: number;
}

const MercadoCalendario: React.FC<Props> = ({ propiedadId, nombre, refreshToken = 0 }) => {
  const [mode, setMode] = useState<"month" | "year">("month");
  // Cursor = mes (o año) que se está viendo. Arranca en el mes actual.
  const [cursor, setCursor] = useState<Dayjs>(() => ahoraChile().startOf("month"));
  const [data, setData] = useState<MercadoCalendarioResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [reloadKey, setReloadKey] = useState(0);
  const [selDia, setSelDia] = useState<string | null>(null);
  // Clave de los datos que se están MOSTRANDO (propiedad, rango y reintento de la
  // última carga que respondió bien). Solo es refresco silencioso si el efecto
  // corre con esa misma clave: un refresco que llega con una carga normal en
  // vuelo (o antes de la primera respuesta) es otra carga normal, y si falla
  // muestra el error y "Reintentar" en vez de un falso "sin datos".
  const claveDatosRef = React.useRef<string | null>(null);

  const paleta = useMemo(() => getPaleta(data?.paletaId), [data?.paletaId]);

  const { desde, hasta } = useMemo(() => {
    if (mode === "year") {
      return {
        desde: cursor.startOf("year").format("YYYY-MM-DD"),
        hasta: cursor.endOf("year").format("YYYY-MM-DD"),
      };
    }
    return {
      desde: cursor.startOf("month").format("YYYY-MM-DD"),
      hasta: cursor.endOf("month").format("YYYY-MM-DD"),
    };
  }, [mode, cursor]);

  useEffect(() => {
    let cancelado = false;
    const clave = `${propiedadId}|${desde}|${hasta}|${reloadKey}`;
    const silencioso = claveDatosRef.current === clave;
    (async () => {
      if (!silencioso) {
        setLoading(true);
        setError(false);
        setSelDia(null);
      }
      const res = await mercadoApi.getCalendario(propiedadId, desde, hasta);
      if (!cancelado) {
        // apiFetch retorna null ante error (red/500/timeout). NO pisamos datos
        // buenos con un vacío falso: en una carga normal marcamos error y
        // mostramos "reintentar"; en un refresco silencioso se conserva lo visible.
        if (res) {
          setData(res);
          setError(false);
          claveDatosRef.current = clave;
        } else if (!silencioso) {
          setError(true);
        }
        setLoading(false);
      }
    })();
    return () => {
      cancelado = true;
    };
  }, [propiedadId, desde, hasta, reloadKey, refreshToken]);

  // Mapa día (YYYY-MM-DD) → estadías que lo cubren. Cada combo se expande sobre
  // las NOCHES de su tramo `[checkin, checkout)` (el día de salida NO se cuenta:
  // el huésped se va esa mañana y esa noche pertenece a la próxima estadía), lo
  // que además evita el "doble cartel" en días de recambio (checkout de A =
  // check-in de B). Acotado a la ventana visible. Solo combos con OCUPACIÓN no
  // nula (los de datos insuficientes se muestran como "sin datos aún").
  const porDia = useMemo(() => {
    const m = new Map<string, MercadoFechaCliente[]>();
    const winStart = dayjs(desde);
    const winEndExcl = dayjs(hasta).add(1, "day"); // `hasta` es inclusivo
    for (const d of data?.dias ?? []) {
      if (d.ocupacion == null) continue;
      let cur = dayjs(d.checkin);
      if (cur.isBefore(winStart)) cur = winStart;
      const finExcl = dayjs(d.checkout); // checkout EXCLUIDO (no es una noche)
      const stop = finExcl.isBefore(winEndExcl) ? finExcl : winEndExcl;
      let guard = 0;
      while (cur.isBefore(stop) && guard < 400) {
        const key = cur.format("YYYY-MM-DD");
        const arr = m.get(key) ?? [];
        arr.push(d);
        m.set(key, arr);
        cur = cur.add(1, "day");
        guard += 1;
      }
    }
    return m;
  }, [data, desde, hasta]);

  const totalConDatos = porDia.size;

  // Avisos informativos (round 2026-07-23): banner de los del período visible +
  // mapa por día para el marcador y el detalle.
  const notas = useMemo(() => data?.notas ?? [], [data?.notas]);
  const avisosBanner = useMemo(() => notasEnRango(notas, desde, hasta), [notas, desde, hasta]);
  const notasDelDia = useMemo(() => notasPorDia(notas, desde, hasta), [notas, desde, hasta]);

  const irHoy = () => {
    setCursor(mode === "year" ? ahoraChile().startOf("year") : ahoraChile().startOf("month"));
  };
  const paso = (dir: 1 | -1) => {
    setCursor((c) => (mode === "year" ? c.add(dir, "year") : c.add(dir, "month")));
  };

  // ── Celdas del mes (6 semanas fijas = 42 días, arranca en lunes) ──────────
  const diasMes = useMemo(() => {
    const first = cursor.startOf("month");
    const offset = (first.day() + 6) % 7; // días desde el lunes
    const start = first.subtract(offset, "day");
    return Array.from({ length: 42 }, (_, i) => start.add(i, "day"));
  }, [cursor]);

  const renderMes = () => (
    <div className="mercado-cal-grid">
      {DOW.map((d) => (
        <div key={d} className="mercado-cal-dow">
          {d}
        </div>
      ))}
      {diasMes.map((d) => {
        const key = d.format("YYYY-MM-DD");
        const enMes = d.month() === cursor.month();
        const combos = porDia.get(key);
        const rep = combos ? repOcupacion(combos) : null;
        const ocup = rep?.ocupacion ?? null;
        const color = ocup != null ? colorDeOcupacion(paleta, ocup) : null;
        const tieneAviso = enMes && notasDelDia.has(key);
        const clickable = enMes && (!!rep || tieneAviso);
        return (
          <div
            key={key}
            className={`mercado-cal-cell${enMes ? "" : " mercado-cal-cell--out"}${
              clickable ? " mercado-cal-cell--data" : ""
            }${selDia === key ? " mercado-cal-cell--sel" : ""}`}
            style={enMes && color ? { background: tinte(color, 0.28) } : undefined}
            onClick={clickable ? () => setSelDia((s) => (s === key ? null : key)) : undefined}
          >
            <span className="mercado-cal-daynum">{d.date()}</span>
            {tieneAviso && (
              <Tooltip title={notasDelDia.get(key)!.map((n) => n.titulo).join(" · ")}>
                <PushpinFilled className="mercado-cal-aviso-pin" />
              </Tooltip>
            )}
            {enMes && ocup != null && (
              <Tooltip title={nivelLabel(nivelDeOcupacion(ocup))}>
                <span className="mercado-cal-ocup">{redondearOcupacion10(ocup)}%</span>
              </Tooltip>
            )}
          </div>
        );
      })}
    </div>
  );

  // ── Vista anual: 12 mini-meses (heatmap de puntos) ────────────────────────
  const renderAnio = () => (
    <div className="mercado-cal-year">
      {MESES.map((nombreMes, m) => {
        const first = cursor.year(cursor.year()).month(m).startOf("month");
        const offset = (first.day() + 6) % 7;
        const start = first.subtract(offset, "day");
        const dias = Array.from({ length: 42 }, (_, i) => start.add(i, "day"));
        const tieneAlgo = dias.some((d) => d.month() === m && porDia.has(d.format("YYYY-MM-DD")));
        return (
          <div
            key={m}
            className={`mercado-cal-mini${tieneAlgo ? " mercado-cal-mini--data" : ""}`}
            onClick={() => {
              setCursor(first);
              setMode("month");
            }}
            role="button"
          >
            <div className="mercado-cal-mini-title">{nombreMes}</div>
            <div className="mercado-cal-mini-grid">
              {dias.map((d) => {
                const key = d.format("YYYY-MM-DD");
                const enMes = d.month() === m;
                const combos = enMes ? porDia.get(key) : undefined;
                const rep = combos ? repOcupacion(combos) : null;
                const color = rep?.ocupacion != null ? colorDeOcupacion(paleta, rep.ocupacion) : null;
                return (
                  <span
                    key={key}
                    className={`mercado-cal-mini-cell${enMes ? "" : " mercado-cal-mini-cell--out"}`}
                    style={color ? { background: color } : undefined}
                  />
                );
              })}
            </div>
          </div>
        );
      })}
    </div>
  );

  // ── Detalle del día seleccionado ──────────────────────────────────────────
  // Detalle del día: los AVISOS del día (arriba) + UN solo cartel de demanda (el
  // representante = mayor ocupación), para no mostrar dos carteles en días de
  // recambio (round 2026-07-23).
  const detalle = () => {
    if (!selDia) return null;
    const rep = repOcupacion(porDia.get(selDia) ?? []);
    const avisosDia = notasDelDia.get(selDia) ?? [];
    if (!rep && !avisosDia.length) return null;
    const ocup = rep?.ocupacion ?? 0;
    const nivel = nivelDeOcupacion(ocup);
    return (
      <div className="mercado-cal-detail">
        <div className="mercado-cal-detail-title">
          {dayjs(selDia).format("dddd DD [de] MMMM YYYY")}
        </div>
        {avisosDia.map((n) => (
          <div key={`aviso-${n.id}`} className="mercado-cal-aviso">
            <PushpinFilled className="mercado-cal-aviso-icon" />
            <div>
              <div className="mercado-cal-aviso-titulo">{n.titulo}</div>
              {n.mensaje && <div className="mercado-cal-aviso-msg">{n.mensaje}</div>}
            </div>
          </div>
        ))}
        {rep && (
          <div className="mercado-cal-detail-combo">
            <div className="mercado-cal-detail-head">
              <span
                className="mercado-demanda-chip"
                style={{ ["--mercado-demanda-color" as string]: colorDeNivel(paleta, nivel) }}
              >
                <span className="mercado-demanda-dot" aria-hidden />
                {nivelLabel(nivel)}
              </span>
              <span className="mercado-cal-ocup-grande">
                Ocupación estimada del mercado: <b>{redondearOcupacion10(ocup)}%</b>
              </span>
              {rep.confianza === "baja" && <Tag color="orange">Datos preliminares</Tag>}
            </div>
            <div className="mercado-cal-detail-noches">
              Estadía {dayjs(rep.checkin).format("DD MMM")} → {dayjs(rep.checkout).format("DD MMM")}{" "}
              · {rep.noches} {rep.noches === 1 ? "noche" : "noches"}
            </div>
            <div className="mercado-cal-detail-msg">{rep.mensaje}</div>
          </div>
        )}
      </div>
    );
  };

  // Banner de avisos del período visible (arriba del calendario).
  const rangoNotaLabel = (n: { fechaInicio: string; fechaFin: string }) => {
    const ini = dayjs(n.fechaInicio);
    return n.fechaInicio === n.fechaFin
      ? ini.format("DD MMM")
      : `${ini.format("DD MMM")} – ${dayjs(n.fechaFin).format("DD MMM")}`;
  };
  const bannerAvisos = () =>
    avisosBanner.length === 0 ? null : (
      <div className="mercado-cal-avisos-banner">
        {avisosBanner.map((n) => (
          <div key={n.id} className="mercado-cal-aviso">
            <PushpinFilled className="mercado-cal-aviso-icon" />
            <div>
              <div className="mercado-cal-aviso-titulo">
                {n.titulo}
                <span className="mercado-cal-aviso-fechas"> · {rangoNotaLabel(n)}</span>
              </div>
              {n.mensaje && <div className="mercado-cal-aviso-msg">{n.mensaje}</div>}
            </div>
          </div>
        ))}
      </div>
    );

  // ── Estados vacíos / carga / error ────────────────────────────────────────
  if (loading && !data) {
    return <Skeleton active paragraph={{ rows: 8 }} />;
  }
  if (error) {
    return (
      <Alert
        type="error"
        showIcon
        message="No se pudo cargar el calendario de mercado"
        description="Hubo un problema al obtener los datos. Revisa tu conexión e intenta de nuevo."
        action={
          <Button size="small" onClick={() => setReloadKey((k) => k + 1)}>
            Reintentar
          </Button>
        }
      />
    );
  }
  // Los guards de habilitado/propiedad se gatean con !loading para no mostrar un
  // vacío obsoleto mientras se recarga otra propiedad/mes.
  if (!loading && data && data.habilitado === false) {
    return (
      <Empty style={{ padding: 32 }} description="Esta sección estará disponible próximamente." />
    );
  }
  if (!loading && data && data.propiedad === null) {
    return (
      <Empty
        style={{ padding: 32 }}
        description="No hay datos de mercado disponibles para esta propiedad."
      />
    );
  }

  const tituloRango = mode === "year" ? cursor.format("YYYY") : `${MESES[cursor.month()]} ${cursor.year()}`;

  return (
    <div className="mercado-cal">
      <div className="mercado-cal-toolbar">
        <Space>
          <Button icon={<LeftOutlined />} onClick={() => paso(-1)} aria-label="Anterior" />
          <span className="mercado-cal-title">{nombre ? `${nombre} · ` : ""}{tituloRango}</span>
          <Button icon={<RightOutlined />} onClick={() => paso(1)} aria-label="Siguiente" />
          <Button size="small" onClick={irHoy}>
            Hoy
          </Button>
        </Space>
        <Segmented
          value={mode}
          onChange={(v) => setMode(v as "month" | "year")}
          options={[
            { label: "Mes", value: "month" },
            { label: "Año", value: "year" },
          ]}
        />
      </div>

      <div className="mercado-cal-legend">
        {NIVELES_DESC.map((d) => (
          <span key={d}>
            <i style={{ background: colorDeNivel(paleta, d) }} /> {nivelLabel(d)}
          </span>
        ))}
        <span>
          <i className="mercado-cal-legend-empty" /> Sin datos aún
        </span>
      </div>

      {/* Avisos ARRIBA del calendario (banner del período + detalle del día
          seleccionado): van antes de la grilla para que se lean sí o sí. */}
      {bannerAvisos()}
      {mode === "month" && detalle()}

      {/* Al navegar mes/año se recarga la data (loading=true). NO cambiamos la
          grilla por un Skeleton: eso colapsa la altura del documento y el scroll
          salta hacia arriba. En su lugar mantenemos la grilla montada (misma
          altura: 42 celdas derivadas solo del cursor) y solo la atenuamos. El
          Skeleton grande de la carga inicial vive en el guard `loading && !data`
          de arriba. El aviso "sin datos" solo aparece ya asentado (!loading),
          para no parpadear mientras la data del nuevo período está en vuelo. */}
      <div className={`mercado-cal-body${loading ? " mercado-cal-body--loading" : ""}`}>
        {mode === "year" ? (
          renderAnio()
        ) : totalConDatos === 0 && !loading ? (
          <Alert
            type="info"
            showIcon
            style={{ margin: "8px 0" }}
            message="Sin datos para este período"
            description="Todavía no hay mediciones de mercado para estas fechas. Prueba con otro mes o año (los días con datos aparecen pintados)."
          />
        ) : (
          renderMes()
        )}
      </div>
    </div>
  );
};

export default MercadoCalendario;
